/**
 * useObstructionDetailsSectionTabs
 *
 * Builds the section navigation entries for the obstruction details panel.
 * Mirrors `useTaskDetailsSectionTabs`:
 *
 * - The `general` and `history` tabs are always present.
 * - The `contractor` tab appears when the obstruction has at least one
 *   contractor-related field filled. Wave 1 always seeds company/manager/phone
 *   (required by the backend contract), so in practice this is always true,
 *   but we keep the conditional gate to mirror the tasks pattern for future
 *   data shapes (e.g. legacy migrated rows).
 * - The Geometry tab is intentionally NOT exposed here — it lands with the
 *   Phase 2 multi-geometry editor.
 */
import { useMemo } from 'react';
import {
  IconBuilding,
  IconHistory,
  IconInfoCircle,
  IconPaperclip,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import type { CollapsibleTabItem } from '@/components/common/CollapsibleTabs';

import type { ObstructionDetailTabType } from './useObstructionDetailsState';

interface UseObstructionDetailsSectionTabsOptions {
  /** Whether the contractor data block has any populated fields. */
  hasContractor: boolean;
  /** Whether the obstruction has at least one attachment. */
  hasAttachments: boolean;
}

interface UseObstructionDetailsSectionTabsResult {
  sectionTabs: CollapsibleTabItem[];
  availableTabs: readonly ObstructionDetailTabType[];
}

export function useObstructionDetailsSectionTabs({
  hasContractor,
  hasAttachments,
}: UseObstructionDetailsSectionTabsOptions): UseObstructionDetailsSectionTabsResult {
  const { t } = useTranslation();

  return useMemo<UseObstructionDetailsSectionTabsResult>(() => {
    const sectionTabs: CollapsibleTabItem[] = [
      {
        id: 'general',
        label: t('obstructions.detail.tabs.general'),
        icon: IconInfoCircle,
      },
      ...(hasContractor
        ? [
            {
              id: 'contractor',
              label: t('obstructions.detail.tabs.contractor'),
              icon: IconBuilding,
            },
          ]
        : []),
      ...(hasAttachments
        ? [
            {
              id: 'attachments',
              label: t('obstructions.detail.tabs.attachments'),
              icon: IconPaperclip,
            },
          ]
        : []),
      {
        id: 'history',
        label: t('obstructions.detail.tabs.history'),
        icon: IconHistory,
      },
    ];

    const availableTabs = sectionTabs.map(
      (tab) => tab.id as ObstructionDetailTabType
    );

    return { sectionTabs, availableTabs };
  }, [t, hasContractor, hasAttachments]);
}
